import React, { useState, useRef, useEffect } from 'react'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { runDantzigMin } from './function/Minimisation'
import { runDantzigMax } from './function/Maximisation'
import Graphe from './view/Graphe'
import SommetDistance from './view/SommetDistance'
import Etape from './view/Etape'
import { graphUtils } from './function/graphUtils'
import '@/css/dantzig.css'
import '@/css/colorBouton.css'

import { drawGraph, getNodeScale } from './function/graphRendering'

const styles = {
  graphContainer: {
    position: 'relative',
    height: '450px',
    backgroundColor: 'white',
    border: '1px solid #ccc',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  controlPanel: {
    backgroundColor: '#f0f0f0',
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '10px'
  }
}

const Dantzig = () => {
  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])
  const [selectedNode, setSelectedNode] = useState(null)
  const [secondSelectedNode, setSecondSelectedNode] = useState(null)
  const [edgeWeight, setEdgeWeight] = useState('')
  const [showEdgeForm, setShowEdgeForm] = useState(false)
  const [nodeName, setNodeName] = useState('')
  const [path, setPath] = useState([])
  const [showSteps, setShowSteps] = useState(false)
  const [steps, setSteps] = useState([])
  const [startNode, setStartNode] = useState(null)
  const [endNode, setEndNode] = useState(null)
  const canvasRef = useRef(null)
  const [afficheMin, setAfficheMin] = useState(false)

  // Réf pour mémoriser la dernière taille connue du conteneur du graphe
  const containerSizeRef = useRef({ width: 0, height: 0 })

  // Réf pour suivre le sommet en cours de déplacement (souris OU tactile)
  const draggingRef = useRef(null) // { nodeId, pointerId }

  // Fonction pour ajouter un nouveau sommet
  const addNode = () => {
    if (!nodeName.trim()) {
      toast.error("Veuillez entrer un nom pour le sommet", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }

    if (nodes.some(node => node.name === nodeName)) {
      toast.error("Ce nom de sommet existe déjà", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }

    const canvas = canvasRef.current
    // getBoundingClientRect = taille CSS réellement affichée, fiable sur tous les écrans
    const rect = canvas.getBoundingClientRect()
    const margin = 25
    const newNode = {
      id: Date.now(),
      name: nodeName,
      x: Math.random() * Math.max(1, rect.width - margin * 2) + margin,
      y: Math.random() * Math.max(1, rect.height - margin * 2) + margin,
    }

    setNodes([...nodes, newNode])
    setNodeName('')
  }

  const startAddingEdge = (node) => {
    if (selectedNode === null) {
      setSelectedNode(node);
    } else if (selectedNode.id !== node.id) {
      setSecondSelectedNode(node);
      setShowEdgeForm(true);
    }
  }

  const addEdge = () => {
    if (!edgeWeight || isNaN(Number(edgeWeight)) || Number(edgeWeight) <= 0) {
      toast.error("Veuillez entrer une distance valide (nombre positif)", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }

    const weight = Number(edgeWeight)

    const edgeExists = edges.some(
      edge => edge.source === selectedNode.id && edge.target === secondSelectedNode.id
    )

    if (edgeExists) {
      toast.error("Cette arête existe déjà dans cette direction", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      resetEdgeSelection()
      return
    }

    const newEdge = {
      id: `${selectedNode.id}-${secondSelectedNode.id}`,
      source: selectedNode.id,
      target: secondSelectedNode.id,
      weight: weight
    }

    setEdges([...edges, newEdge])
    resetEdgeSelection()
  }

  const resetEdgeSelection = () => {
    setSelectedNode(null)
    setSecondSelectedNode(null)
    setEdgeWeight('')
    setShowEdgeForm(false)
  }

  const deleteNode = (nodeId) => {
    setNodes(nodes.filter(node => node.id !== nodeId))
    setEdges(edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId))
    resetEdgeSelection()
  }

  const deleteEdge = (edgeId) => {
    setEdges(edges.filter(edge => edge.id !== edgeId))
  }

  useEffect(() => {
    let allPaths = null
    if (steps.length > 0) {
      const lastStep = steps[steps.length - 1]
      if (lastStep.allMinimalPaths) {
        allPaths = lastStep.allMinimalPaths
      } else if (lastStep.allMaximalPaths) {
        allPaths = lastStep.allMaximalPaths
      }
    }

    drawGraph(
      canvasRef, nodes, edges, selectedNode, secondSelectedNode, path,
      startNode, endNode, showSteps, steps, afficheMin, allPaths
    )
  }, [nodes, edges, selectedNode, secondSelectedNode, path, startNode, endNode, showSteps, steps, afficheMin])

  // ---- Rendre le graphe responsive ----
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const container = canvas.parentElement

    if (containerSizeRef.current.width === 0) {
      const rect = container.getBoundingClientRect()
      containerSizeRef.current = { width: rect.width, height: rect.height }
    }

    const resyncCanvas = () => {
      const rect = container.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        canvas.width = rect.width
        canvas.height = rect.height
      }
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        const { width: oldWidth, height: oldHeight } = containerSizeRef.current

        // Toujours resynchroniser la taille interne du canvas avec sa taille CSS réelle
        // (corrige les cas où canvas.width reste désynchronisé, ex: barre d'adresse mobile)
        resyncCanvas()

        if (
          oldWidth > 0 && oldHeight > 0 &&
          (Math.abs(width - oldWidth) > 0.5 || Math.abs(height - oldHeight) > 0.5)
        ) {
          const scaleX = width / oldWidth
          const scaleY = height / oldHeight

          setNodes(prevNodes =>
            prevNodes.map(node => ({
              ...node,
              x: node.x * scaleX,
              y: node.y * scaleY
            }))
          )
        }

        containerSizeRef.current = { width, height }
      }
    })

    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])
  // ---- FIN ----

  // ---- Déplacement des sommets : souris ET tactile via Pointer Events ----
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, rect }
  }

  const handlePointerDown = (e) => {
    const { x, y, rect } = getCanvasCoords(e)
    const scale = getNodeScale(rect.width)
    const clickRadius = Math.max(16, 20 * scale) // 16px min pour rester "tapable" au doigt

    const clickedNode = nodes.find(
      node => Math.sqrt((node.x - x) ** 2 + (node.y - y) ** 2) < clickRadius
    )

    if (!clickedNode) return

    if (e.shiftKey) {
      // Création d'arête (clavier physique requis, donc surtout desktop)
      startAddingEdge(clickedNode)
      return
    }

    // Démarrer le déplacement
    e.preventDefault()
    const canvas = canvasRef.current
    try {
      canvas.setPointerCapture(e.pointerId)
    } catch (err) {
      // certains navigateurs peuvent ne pas supporter setPointerCapture, on continue quand même
    }
    draggingRef.current = { nodeId: clickedNode.id, pointerId: e.pointerId }
  }

  const handlePointerMove = (e) => {
    const dragging = draggingRef.current
    if (!dragging || dragging.pointerId !== e.pointerId) return
    e.preventDefault()

    const { x, y, rect } = getCanvasCoords(e)
    const clampedX = Math.min(Math.max(x, 0), rect.width)
    const clampedY = Math.min(Math.max(y, 0), rect.height)

    setNodes(prevNodes =>
      prevNodes.map(n =>
        n.id === dragging.nodeId ? { ...n, x: clampedX, y: clampedY } : n
      )
    )
  }

  const handlePointerUp = (e) => {
    const dragging = draggingRef.current
    if (!dragging || dragging.pointerId !== e.pointerId) return
    const canvas = canvasRef.current
    try {
      canvas.releasePointerCapture(e.pointerId)
    } catch (err) {
      // no-op
    }
    draggingRef.current = null
  }
  // ---- FIN ----

  const handleRunDantzigMin = () => {
    runDantzigMin(
      nodes, edges, startNode, endNode, setPath, setSteps,
      setShowSteps, setAfficheMin
    )
  }

  const handleRunDantzigMax = () => {
    runDantzigMax(
      nodes, edges, startNode, endNode, setPath, setSteps,
      setShowSteps, setAfficheMin
    )
  }

  const selectStartNode = (node) => {
    setStartNode(node)
    if (node.id === endNode?.id) {
      setEndNode(null)
    }
  }

  const selectEndNode = (node) => {
    setEndNode(node)
    if (node.id === startNode?.id) {
      setStartNode(null)
    }
  }

  const resetAlgorithm = () => {
    setPath([])
    setShowSteps(false)
    setSteps([])
    setStartNode(null)
    setEndNode(null)
  }

  return (
    <>
      <ToastContainer/>
      <div className="p-1 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-4">

          <Graphe
            nodeName={nodeName} setNodeName={setNodeName} addNode={addNode}
            startNode={startNode} endNode={endNode} nodes={nodes}
            selectStartNode={selectStartNode} selectEndNode={selectEndNode}
            resetAlgorithm={resetAlgorithm}
            showEdgeForm={showEdgeForm} selectedNode={selectedNode}
            secondSelectedNode={secondSelectedNode} edgeWeight={edgeWeight}
            setEdgeWeight={setEdgeWeight} addEdge={addEdge}
            resetEdgeSelection={resetEdgeSelection} canvasRef={canvasRef}
            handlePointerDown={handlePointerDown}
            handlePointerMove={handlePointerMove}
            handlePointerUp={handlePointerUp}
            handleRunDantzigMax={handleRunDantzigMax}
            handleRunDantzigMin={handleRunDantzigMin}
            exportGraphAsImage={() => graphUtils.exportGraphAsImage(canvasRef)}
            generateExample={() => graphUtils.generateExample(
              canvasRef, setNodes, setEdges, setStartNode, setEndNode,
              resetEdgeSelection, setPath, setShowSteps, setSteps
            )}
            styles={styles}
          />

          <SommetDistance nodes={nodes} edges={edges} deleteNode={deleteNode} deleteEdge={deleteEdge} />

        </div>

        <Etape showSteps={showSteps} steps={steps} afficheMin={afficheMin} path={path} />

      </div>
    </>
  )
}

export default Dantzig